import { FC } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { Assessment, GetAllAssessmentsQueries } from '@libs/types';

import { AssessmentTableRow } from './AssessmentTableRow';

interface AssessmentsTableProps {
  assessments: Assessment[];
  params?: Partial<GetAllAssessmentsQueries>;
}

const HEADER_KEYS = [
  'app.assessments.table.name',
  'app.assessments.table.status',
  'app.assessments.table.progress',
  'app.assessments.table.score',
  'app.assessments.table.submitted',
  'app.assessments.table.completed',
  'app.assessments.table.created',
  'app.assessments.table.updated',
  'app.assessments.table.actions',
] as const;

export const AssessmentsTable: FC<AssessmentsTableProps> = observer(({ assessments, params }) => {
  const theme = useTheme();

  return (
    <TableContainer
      component={Paper}
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: 'none',
        overflow: 'auto',
      }}
    >
      {/* Column widths: rebalanced so the Status badge ("Completed") fits
          without ellipsis and the Name column has more room. The Actions
          column can be narrow because it now holds one primary button
          plus the kebab menu (the inline "Start New Version" button moved
          into the menu). minWidth keeps the table readable on small screens
          — beyond that the container scrolls horizontally. */}
      <Table sx={{ width: '100%', minWidth: 1180, tableLayout: 'fixed' }} stickyHeader>
        <colgroup>
          <col style={{ width: '24%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            {HEADER_KEYS.map((key, idx) => (
              <TableCell
                key={key}
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  paddingY: 1.5,
                  paddingLeft: idx === 0 ? 2 : 1,
                  paddingRight: idx === HEADER_KEYS.length - 1 ? 3 : 1,
                  textAlign: 'start',
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                  }}
                >
                  <Trans i18nKey={key} />
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {assessments.map((assessment, index) => (
            <AssessmentTableRow
              key={assessment.id}
              assessment={assessment}
              index={index}
              params={params}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
